import { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useAuth } from "../contexts/AuthContext";
import RoleBasedLayout, {
  type LayoutMenuItem,
} from "../components/RoleBasedLayout";
import {
  LayoutDashboard,
  Building,
  BookOpen,
  BarChart3,
  DollarSign,
  FileText,
  Users,
  Headphones,
  Mail,
  Truck,
  MapPin,
  GraduationCap,
  PlayCircle,
  Calendar,
  Layers,
  Image,
  Newspaper,
  HelpCircle,
  MessageSquare,
  Package,
  Globe,
  Map,
  Building2,
  Locate,
} from "lucide-react";
import Dashboard from "./super-admin/Dashboard";
import Hostels from "./super-admin/Hostels";
import Institutions from "./super-admin/Institutions";
import InstitutionDetail from "./super-admin/InstitutionDetail";
import AddInstitution from "./super-admin/AddInstitution";
import Scholarships from "./super-admin/Scholarships";
import Reports from "./super-admin/Reports";
import Funding from "./super-admin/Funding";
import UserManagement from "./super-admin/UserManagement";
import Crm from "./super-admin/CRM";
import ContactQueries from "./super-admin/ContactQueries";
import Podcasts from "./super-admin/cms/Podcasts";
import Events from "./super-admin/cms/Events";
import Blogs from "./super-admin/cms/Blogs";
import MediaGallery from "./super-admin/cms/MediaGallery";
import PressCoverage from "./super-admin/cms/PressCoverage";
import Vendors from "./super-admin/Vendors";
import CategoriesSubCategories from "./super-admin/CategoriesSubCategories";
import ListMastersIndex from "./super-admin/list-masters/index";
import CountryPage from "./super-admin/list-masters/Country";
import ZonePage from "./super-admin/list-masters/Zone";
import StatePage from "./super-admin/list-masters/State";
import CityPage from "./super-admin/list-masters/City";
import AreaPage from "./super-admin/list-masters/Area";
import FAQManagement from "./super-admin/FAQManagement";
import TestimonialsManagement from "./super-admin/TestimonialsManagement";
import StudentInquiries from "./super-admin/StudentInquiries";

const superAdminMenuItems: LayoutMenuItem[] = [
  {
    icon: LayoutDashboard,
    label: "Dashboard",
    path: "/super-admin/dashboard",
    superAdminOnly: true,
  },
  { icon: Building, label: "Institutions", path: "/super-admin/institutions" },
  {
    icon: GraduationCap,
    label: "Student Inquiries",
    path: "/super-admin/student-inquiries",
    superAdminOnly: true,
  },
  {
    icon: Truck,
    label: "Vendors",
    path: "/super-admin/vendors",
    subItems: [
      {
        label: "Vendors Management",
        path: "/super-admin/vendors",
        icon: Truck,
      },
      { label: "Categories", path: "/super-admin/categories", icon: Package },
    ],
  },
  {
    icon: MapPin,
    label: "List Masters",
    path: "/super-admin/list-masters",
    subItems: [
      {
        label: "Country",
        path: "/super-admin/list-masters/country",
        icon: Globe,
      },
      { label: "Zone", path: "/super-admin/list-masters/zone", icon: Layers },
      { label: "State", path: "/super-admin/list-masters/state", icon: Map },
      {
        label: "City",
        path: "/super-admin/list-masters/city",
        icon: Building2,
      },
      { label: "Area", path: "/super-admin/list-masters/area", icon: Locate },
    ],
  },
  { icon: BookOpen, label: "Scholarships", path: "/super-admin/scholarships" },
  { icon: BarChart3, label: "Reports", path: "/super-admin/reports" },
  { icon: DollarSign, label: "Donation Status", path: "/super-admin/funding" },
  {
    icon: FileText,
    label: "CMS Pages",
    path: "/super-admin/cms",
    subItems: [
      {
        label: "Podcasts",
        path: "/super-admin/cms/podcasts",
        icon: PlayCircle,
      },
      { label: "Events", path: "/super-admin/cms/events", icon: Calendar },
      { label: "Blogs", path: "/super-admin/cms/blogs", icon: Layers },
      { label: "Media Gallery", path: "/super-admin/cms/media", icon: Image },
      {
        label: "Press Coverage",
        path: "/super-admin/cms/press",
        icon: Newspaper,
      },
      { label: "FAQ Management", path: "/super-admin/faq", icon: HelpCircle },
      {
        label: "Testimonials",
        path: "/super-admin/testimonials",
        icon: MessageSquare,
      },
    ],
  },
  {
    icon: Users,
    label: "User Management",
    path: "/super-admin/user-management",
    superAdminOnly: true,
  },
  { icon: Headphones, label: "CRM Module", path: "/super-admin/crm" },
  {
    icon: Mail,
    label: "Contact Us",
    path: "/super-admin/contact",
    superAdminOnly: true,
  },
];

export default function SuperAdminDashboard() {
  const { userRole, loading } = useAuth();
  const navigate = usePrefixedNavigate();

  const isSuperAdmin = userRole === "super_admin";
  const menuItems = superAdminMenuItems.filter(
    item => !item.superAdminOnly || isSuperAdmin
  );

  useEffect(() => {
    // Only redirect if loading is complete and role doesn't match
    if (import.meta.env.DEV) {
      console.log("SuperAdminDashboard Auth State:", { loading, userRole });
    }
    if (
      !loading &&
      userRole &&
      userRole !== "super_admin" &&
      userRole !== "management"
    ) {
      if (import.meta.env.DEV) {
        console.warn(
          "Redirecting to portal-selection due to mismatching role:",
          userRole
        );
      }
      navigate("/portal-selection");
    }
  }, [userRole, loading, navigate]);

  return (
    <RoleBasedLayout
      menuItems={menuItems}
      sidebarTitle={isSuperAdmin ? "Super Admin" : "Management"}
      sidebarSubtitle="Control Panel"
    >
      <Routes>
        {/* Routes are relative to /super-admin/* parent route */}
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/hostels" element={<Hostels />} />
        <Route path="/institutions" element={<Institutions />} />
        <Route path="/student-inquiries" element={<StudentInquiries />} />
        <Route path="/institutions/:id" element={<InstitutionDetail />} />
        <Route path="/institutions/add" element={<AddInstitution />} />
        <Route path="/scholarships" element={<Scholarships />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/funding" element={<Funding />} />
        <Route path="/user-management" element={<UserManagement />} />
        <Route path="/crm" element={<Crm />} />
        <Route path="/contact" element={<ContactQueries />} />
        <Route path="/vendors" element={<Vendors />} />
        <Route path="/categories" element={<CategoriesSubCategories />} />
        <Route path="/faq" element={<FAQManagement />} />
        <Route path="/testimonials" element={<TestimonialsManagement />} />
        <Route path="/list-masters" element={<ListMastersIndex />} />
        <Route path="/list-masters/country" element={<CountryPage />} />
        <Route path="/list-masters/zone" element={<ZonePage />} />
        <Route path="/list-masters/state" element={<StatePage />} />
        <Route path="/list-masters/city" element={<CityPage />} />
        <Route path="/list-masters/area" element={<AreaPage />} />
        <Route path="/cms/podcasts" element={<Podcasts />} />
        <Route path="/cms/events" element={<Events />} />
        <Route path="/cms/blogs" element={<Blogs />} />
        <Route path="/cms/media" element={<MediaGallery />} />
        <Route path="/cms/press" element={<PressCoverage />} />
      </Routes>
    </RoleBasedLayout>
  );
}
